import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useNotifications, Notification } from "@/context/NotificationContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  BellRing,
  CheckCircle2,
  Trash2,
  AlertCircle,
  InfoIcon,
  CheckCheck,
  Home,
  MoreVertical,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useDeviceType } from "@/hooks/use-mobile";
import { MobileContainer, MobileSpacing } from "@/components/mobile/ResponsiveLayout";
import MobileCard from "@/components/mobile/MobileCard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Notifications = () => {
  const navigate = useNavigate();
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    fetchingNotfications,
  } = useNotifications();
  const [activeTab, setActiveTab] = useState("all");
  const { isMobile } = useDeviceType();

  const getFilteredNotifications = () => {
    if (activeTab === "all") return notifications;
    if (activeTab === "unread")
      return notifications.filter((notification) => !notification.read);
    return notifications;
  };

  const filteredNotifications = getFilteredNotifications();

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "info":
      default:
        return <InfoIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  if (isMobile) {
    return (
      <MobileContainer>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <nav className="flex items-center text-sm text-muted-foreground mb-1" aria-label="Breadcrumb">
                <Link to="/" className="hover:underline text-primary font-medium flex items-center">
                  <Home className="h-4 w-4 mr-1" />
                </Link>
                <span className="mx-2">/</span>
                <span className="text-foreground font-semibold">Notifications</span>
              </nav>
              <h1 className="text-2xl font-bold">Notifications</h1>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={markAllAsRead}
                  disabled={!notifications.some((n) => !n.read)}
                >
                  <CheckCheck className="mr-2 h-4 w-4" />
                  Mark all as read
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={clearAllNotifications}
                  disabled={notifications.length === 0}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Clear all
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <MobileSpacing size="sm" />

          {/* Tabs */}
          <div className="flex space-x-2">
            <Button
              variant={activeTab === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("all")}
              className="flex-1 h-8"
            >
              All
              <Badge className="ml-2 bg-muted text-muted-foreground h-6 px-2 text-xs uppercase tracking-wide shadow-sm">
                {notifications.length}
              </Badge>
            </Button>
            <Button
              variant={activeTab === "unread" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("unread")}
              className="flex-1 h-8"
            >
              Unread
              <Badge className="ml-2 bg-primary text-primary-foreground h-6 px-2 text-xs uppercase tracking-wide shadow-sm">
                {notifications.filter((n) => !n.read).length}
              </Badge>
            </Button>
          </div>

          {/* Notifications List */}
          {fetchingNotfications ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, idx) => (
                <MobileCard key={idx} className="animate-pulse">
                  <div className="flex items-start space-x-3">
                    <div className="h-5 w-5 bg-muted rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-32 bg-muted rounded" />
                      <div className="h-3 w-48 bg-muted rounded" />
                      <div className="h-3 w-24 bg-muted rounded" />
                    </div>
                  </div>
                </MobileCard>
              ))}
            </div>
          ) : filteredNotifications.length === 0 ? (
            <MobileCard className="text-center py-12">
              <BellRing className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-1">No notifications</h3>
              <p className="text-sm text-muted-foreground">
                {activeTab === "unread"
                  ? "You've read all your notifications."
                  : "You don't have any notifications yet."}
              </p>
            </MobileCard>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => (
                <MobileCard
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 transition-all duration-300 active:scale-[0.98] ${
                    !notification.read 
                      ? "border-l-4 border-l-primary bg-primary/[0.02] shadow-md" 
                      : "opacity-80"
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-medium text-sm leading-5">
                          {notification.title}
                        </h4>
                        <DropdownMenu>
                          <DropdownMenuTrigger 
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0 flex-shrink-0"
                            >
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!notification.read && (
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}>
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                Mark as read
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-3">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </MobileCard>
              ))}
            </div>
          )}

          {/* Bottom spacing for navigation */}
          <div className="h-20" />
        </div>
      </MobileContainer>
    );
  }

  // Desktop layout (original)
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <nav
          className="flex items-center text-sm text-muted-foreground mb-2"
          aria-label="Breadcrumb"
        >
          <Link
            to="/"
            className="hover:underline text-primary font-medium flex items-center"
          >
            <Home className="h-4 w-4 mr-1" />
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground font-semibold">Notifications</span>
        </nav>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            disabled={!notifications.some((n) => !n.read)}
            className="shadow-sm hover:bg-primary/5 transition-all"
          >
            <CheckCheck className="mr-2 h-4 w-4 text-primary" />
            Mark all as read
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllNotifications}
            disabled={notifications.length === 0}
            className="shadow-sm hover:bg-destructive/5 text-destructive hover:text-destructive transition-all"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear all
          </Button>
        </div>
      </div>

      <Tabs defaultValue="all" onValueChange={setActiveTab}>
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="w-full justify-start h-16 p-2 bg-slate-100/80 dark:bg-muted/40 rounded-2xl border shadow-inner gap-2">
            <TabsTrigger 
              value="all"
              className="flex-1 md:flex-none h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground"
            >
              All
              <Badge className="ml-2 bg-muted text-muted-foreground h-6 px-2 text-xs uppercase tracking-wide shadow-sm">
                {notifications.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger 
              value="unread"
              className="flex-1 md:flex-none h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground"
            >
              Unread
              <Badge className="ml-2 bg-primary text-primary-foreground h-6 px-2 text-xs uppercase tracking-wide shadow-sm">
                {notifications.filter((n) => !n.read).length}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={activeTab} className="mt-4">
          {fetchingNotfications ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <Card key={idx} className="animate-pulse">
                  <CardContent className="p-4 flex items-start space-x-4">
                    <div className="h-5 w-5 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-32 bg-muted rounded" />
                      <div className="h-3 w-48 bg-muted rounded" />
                      <div className="h-3 w-24 bg-muted rounded" />
                    </div>
                    <div className="h-8 w-8 bg-muted rounded-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <BellRing className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-xl font-medium mb-1">No notifications</h3>
              <p className="text-muted-foreground">
                {activeTab === "unread"
                  ? "You've read all your notifications."
                  : "You don't have any notifications yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => (
                <Card
                  key={notification.id}
                  className={`cursor-pointer transition-all duration-300 hover:scale-[1.01] hover:shadow-lg ${
                    !notification.read 
                      ? "border-l-4 border-l-primary bg-primary/[0.02] shadow-sophisticated" 
                      : "opacity-80 hover:opacity-100"
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-4 flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium">{notification.title}</h4>
                          {!notification.read && (
                            <Badge className="bg-primary h-6 px-2 text-xs uppercase tracking-wide shadow-sm">New</Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-sm mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDistanceToNow(
                            new Date(notification.timestamp),
                            { addSuffix: true }
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notifications;
